import { useEffect, useState } from "react";
import { api, BACKEND } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import { Save, Loader2, CheckCircle2 } from "lucide-react";

const SLOTS = [
  { key: "home.hero_image", label: "Image principale (Hero)", description: "Photo plein écran tout en haut de la page d'accueil" },
  { key: "home.chef_image", label: "Image \"Notre maison\"", description: "Photo verticale de la section présentation (chef / cuisine)" },
  { key: "home.event_image", label: "Image \"Réceptions privées\"", description: "Photo verticale section événements" },
  { key: "home.plate_image", label: "Image \"Sélection de la carte\"", description: "Photo de plat signature à droite de la sélection menu" },
  { key: "home.terrace_image", label: "Image \"La terrasse\"", description: "Bandeau immersif terrasse" },
];

const resolveUrl = (u) => (u?.startsWith("/api/") ? `${BACKEND}${u}` : u);

export default function HomeImagesAdmin() {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});

  useEffect(() => {
    api.get("/settings").then((r) => setValues(r.data || {}));
  }, []);

  const update = (key) => (url) => setValues((v) => ({ ...v, [key]: url }));

  const save = async (key) => {
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      await api.put(`/settings/${encodeURIComponent(key)}`, { value: values[key] || "" });
      setSaved((s) => ({ ...s, [key]: true }));
      setTimeout(() => setSaved((s) => ({ ...s, [key]: false })), 2500);
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div data-testid="admin-home-images-page">
      <p className="overline">Apparence</p>
      <h1 className="font-display text-4xl md:text-5xl mt-3">Photos de la page d'accueil</h1>
      <p className="text-[#697585] mt-2 max-w-2xl">
        Modifiez les 5 photos clés qui apparaissent sur la page d'accueil. Téléversez de nouvelles images
        ou collez l'URL d'une photo déjà présente dans la galerie.
      </p>

      <div className="mt-10 space-y-10">
        {SLOTS.map((slot) => (
          <div key={slot.key} data-testid={`home-img-slot-${slot.key.split(".")[1]}`} className="border border-[#D9D3C5] bg-[#FFFFFF] p-6">
            <div className="flex justify-between items-start gap-6 flex-wrap">
              <div className="flex-1 min-w-[260px]">
                <p className="overline">{slot.key}</p>
                <h2 className="font-display text-2xl mt-2">{slot.label}</h2>
                <p className="text-sm text-[#697585] mt-1">{slot.description}</p>
                {values[slot.key] && (
                  <div className="mt-4 aspect-[3/2] max-w-md overflow-hidden border border-[#D9D3C5]">
                    <img src={resolveUrl(values[slot.key])} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div className="w-full md:w-[420px]">
                <ImageUploader
                  label="Remplacer"
                  value={values[slot.key]}
                  onChange={update(slot.key)}
                />
                <div className="mt-4 flex justify-end gap-3 items-center">
                  {saved[slot.key] && (
                    <span className="inline-flex items-center gap-1 text-sm text-[#5A7A5A]">
                      <CheckCircle2 size={14} /> Enregistré
                    </span>
                  )}
                  <button
                    data-testid={`home-img-save-${slot.key.split(".")[1]}`}
                    onClick={() => save(slot.key)}
                    disabled={saving[slot.key]}
                    className="btn-primary"
                  >
                    {saving[slot.key] ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Video section */}
        <div data-testid="home-video-slot" className="border border-[#D9D3C5] bg-[#FFFFFF] p-6">
          <div className="flex items-start gap-6 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <p className="overline">home.video</p>
              <h2 className="font-display text-2xl mt-2">Vidéo verticale de la page d'accueil</h2>
              <p className="text-sm text-[#697585] mt-1">
                Vidéo au format 9:16 (Reel) diffusée en autoplay muet dans la section "L'expérience".
                Utilisez un fichier MP4 (H.264) ≤ 10 Mo pour un chargement rapide.
              </p>
              {values["home.video_url"] && (
                <div className="mt-4 relative w-full max-w-[240px] aspect-[9/16] border border-[#D9D3C5] overflow-hidden bg-black">
                  <video
                    key={values["home.video_url"]}
                    src={resolveUrl(values["home.video_url"])}
                    poster={resolveUrl(values["home.video_poster"])}
                    muted
                    loop
                    autoPlay
                    playsInline
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
            <div className="w-full md:w-[420px] space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#697585]">URL de la vidéo (MP4)</label>
                <input
                  className="field mt-2"
                  value={values["home.video_url"] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, "home.video_url": e.target.value }))}
                  placeholder="/entracte-home.mp4 ou https://..."
                  data-testid="home-video-url"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#697585]">URL du poster (image de couverture)</label>
                <input
                  className="field mt-2"
                  value={values["home.video_poster"] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, "home.video_poster": e.target.value }))}
                  placeholder="/entracte-home-poster.jpg"
                  data-testid="home-video-poster"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#697585]">Titre (FR)</label>
                <input className="field mt-2" value={values["home.video_title"] || ""} onChange={(e) => setValues((v) => ({ ...v, "home.video_title": e.target.value }))} data-testid="home-video-title" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#697585]">Titre (EN)</label>
                <input className="field mt-2" value={values["home.video_title_en"] || ""} onChange={(e) => setValues((v) => ({ ...v, "home.video_title_en": e.target.value }))} data-testid="home-video-title-en" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#697585]">Sous-titre (FR)</label>
                <textarea rows={2} className="field mt-2" value={values["home.video_sub"] || ""} onChange={(e) => setValues((v) => ({ ...v, "home.video_sub": e.target.value }))} data-testid="home-video-sub" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-[#697585]">Sous-titre (EN)</label>
                <textarea rows={2} className="field mt-2" value={values["home.video_sub_en"] || ""} onChange={(e) => setValues((v) => ({ ...v, "home.video_sub_en": e.target.value }))} data-testid="home-video-sub-en" />
              </div>
              <div className="flex justify-end gap-3 items-center">
                {(saved["home.video_url"] || saved["home.video_poster"] || saved["home.video_title"]) && (
                  <span className="inline-flex items-center gap-1 text-sm text-[#5A7A5A]">
                    <CheckCircle2 size={14} /> Enregistré
                  </span>
                )}
                <button
                  data-testid="home-video-save-all"
                  onClick={async () => {
                    for (const k of ["home.video_url", "home.video_poster", "home.video_title", "home.video_title_en", "home.video_sub", "home.video_sub_en"]) {
                      await save(k);
                    }
                  }}
                  className="btn-primary"
                >
                  <Save size={14} /> Enregistrer tout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
